import {faStar} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {capitalize} from 'lodash';
import {ReactNode, useMemo} from 'react';

import {Locale} from '../../../../provider/locale/localization';
import {LangKeyType} from '../../../../provider/locale/translation/type';
import {ReviewCatalogType, useReviewsCatalogList} from '../../../../service/reviews/reviews-catalogs';
import {
    TagDataType,
    useAllManualTags,
    useMailingsTags,
    useQRCodesTags,
    useReviewsAspects,
} from '../../../../service/reviews/reviews-tags';
import {
    ReviewsAndAnswersFilterQueriesEnum,
    ReviewsAndAnswersReviewFiltersType,
} from '../../../../service/reviews-and-answers-statistics/filter/reviews-and-answers-statistics-filter-type';
import {
    CONTENT_OF_REVIEWS_OPTIONS,
    INCLUDE_DELETED_OPTIONS,
    POSSIBILITY_OF_AN_ANSWER_OPTIONS,
    REPLIES_TO_REVIEWS_OPTIONS,
    REVIEW_TYPE_OPTIONS,
    SENTIMENT_OPTIONS,
} from '../reviews-and-answers-statistics-review-filter/review-filter-const';

import * as styles from './reviews-and-answers-filter-pdf.scss';

type ReviewFilterPdfChipType = {
    key: string;
    label: ReactNode;
};

type FilterOptionType = {label: JSX.Element; value: unknown};

type AspectType = {ids: Array<number>; title: string};

type TagsDataType = {
    aspects: Array<AspectType> | undefined;
    manualTags: Array<TagDataType> | undefined;
    qrCodeTags: Record<string, string> | undefined;
    mailingsTags: Record<string, string> | undefined;
};

// A tag source group used to collapse long tag lists into a "<group>: <count>" summary.
type TagGroupType = {key: string; label: ReactNode};

type TagEntryType = {group: TagGroupType; chipKey: string; title: ReactNode};

// Above this many tags within one group the report shows a "<group>: <count>" summary
// instead of listing every tag (BST-10539 follow-up).
const MAX_TAGS_PER_GROUP = 5;

const AUTOTAGS_GROUP: TagGroupType = {key: 'autotags', label: <Locale stringKey="REVIEWS__AUTOTAGS_POPUP__HEADER" />};
const QR_CODE_GROUP: TagGroupType = {key: 'qr-code', label: <Locale stringKey="REVIEW__QR_CODE__FILTER__TITLE" />};
const MAILINGS_GROUP: TagGroupType = {key: 'mailings', label: <Locale stringKey="REVIEW__MAILINGS__FILTER__TITLE" />};
const WITHOUT_GROUP: TagGroupType = {key: 'without-group', label: <Locale stringKey="TEXT__WITHOUT_GROUP" />};

// Resolves an option's localized label by its stored value (returns null if not found).
function getOptionLabel(options: ReadonlyArray<FilterOptionType>, value: unknown): JSX.Element | null {
    return options.find((option) => option.value === value)?.label ?? null;
}

// Builds a "<group>: <value>" chip label so each filter reads unambiguously in the report.
function buildChip(key: string, groupStringKey: LangKeyType, value: ReactNode): ReviewFilterPdfChipType {
    return {
        key,
        label: (
            <>
                <Locale stringKey={groupStringKey} />
                {': '}
                {value}
            </>
        ),
    };
}

// Emits a single chip for an option-backed toggle filter, or nothing when the value is unset.
function buildOptionChip(
    key: string,
    groupStringKey: LangKeyType,
    options: ReadonlyArray<FilterOptionType>,
    value: unknown
): Array<ReviewFilterPdfChipType> {
    const label = value ? getOptionLabel(options, value) : null;

    return label ? [buildChip(key, groupStringKey, label)] : [];
}

function buildCatalogChips(
    catalogIds: Array<number>,
    catalogList: Array<ReviewCatalogType> | undefined
): Array<ReviewFilterPdfChipType> {
    return catalogIds.flatMap((id) => {
        const name = (catalogList || []).find((catalog) => catalog.id === id)?.name;

        return name ? [buildChip(`catalog-${id}`, 'REVIEWS__FILTER__LABEL__SOURCE', name)] : [];
    });
}

function buildRatingChips(rating: Array<number>): Array<ReviewFilterPdfChipType> {
    return rating.map((value) => {
        const valueNode =
            value === 0 ? (
                <Locale stringKey="REVIEWS__RADIO__NOT_RATED" />
            ) : (
                <>
                    {value}
                    <FontAwesomeIcon className={styles.FilterPdf_star} icon={faStar} />
                </>
            );

        return buildChip(`rating-${value}`, 'REVIEWS__FILTER__LABEL__RATING', valueNode);
    });
}

function buildSentimentChips(sentiments: Array<string>): Array<ReviewFilterPdfChipType> {
    return sentiments.flatMap((value) => {
        const label = getOptionLabel(SENTIMENT_OPTIONS, value);

        return label ? [buildChip(`sentiment-${value}`, 'REVIEWS__FILTER__LABEL__TONALITY', label)] : [];
    });
}

// Classifies a manual tag by its folder so folder-mates aggregate together.
function getManualTagGroup(tag: TagDataType): TagGroupType {
    return tag.folder ? {key: `folder-${tag.folder.id}`, label: tag.folder.title} : WITHOUT_GROUP;
}

// Maps every selected tag id to a titled entry tagged with its source group.
function collectTagEntries(tagIds: Array<number>, data: TagsDataType): Array<TagEntryType> {
    const {aspects, manualTags, qrCodeTags, mailingsTags} = data;
    const remainingIds = new Set(tagIds);
    const entries: Array<TagEntryType> = [];

    // A selected autotag flattens into several ids — collapse it back to one entry.
    (aspects || []).forEach((aspect) => {
        if (aspect.ids.length > 0 && aspect.ids.every((id) => remainingIds.has(id))) {
            entries.push({
                group: AUTOTAGS_GROUP,
                chipKey: `tag-aspect-${aspect.ids.join('-')}`,
                title: capitalize(aspect.title),
            });
            aspect.ids.forEach((id) => remainingIds.delete(id));
        }
    });

    tagIds.forEach((id) => {
        if (!remainingIds.has(id)) {
            return;
        }

        const manualTag = (manualTags || []).find((tag) => tag.id === id);
        const qrCodeTitle = qrCodeTags?.[id];
        const mailingsTitle = mailingsTags?.[id];

        if (manualTag) {
            entries.push({group: getManualTagGroup(manualTag), chipKey: `tag-${id}`, title: manualTag.title});
        } else if (qrCodeTitle) {
            entries.push({group: QR_CODE_GROUP, chipKey: `tag-${id}`, title: qrCodeTitle});
        } else if (mailingsTitle) {
            entries.push({group: MAILINGS_GROUP, chipKey: `tag-${id}`, title: mailingsTitle});
        } else {
            return;
        }

        remainingIds.delete(id);
    });

    return entries;
}

// Groups tag entries by source group, preserving first-seen order.
function groupTagEntries(entries: Array<TagEntryType>): Array<{group: TagGroupType; entries: Array<TagEntryType>}> {
    const groups: Array<{group: TagGroupType; entries: Array<TagEntryType>}> = [];

    entries.forEach((entry) => {
        const existing = groups.find((item) => item.group.key === entry.group.key);

        if (existing) {
            existing.entries.push(entry);
        } else {
            groups.push({group: entry.group, entries: [entry]});
        }
    });

    return groups;
}

function buildTagChips(tagIds: Array<number>, data: TagsDataType): Array<ReviewFilterPdfChipType> {
    if (tagIds.length === 0) {
        return [];
    }

    return groupTagEntries(collectTagEntries(tagIds, data)).flatMap(({group, entries}) => {
        // Collapse a crowded group to a single "<group>: <count>" chip so the report stays readable.
        if (entries.length > MAX_TAGS_PER_GROUP) {
            return [
                {
                    key: `tag-group-${group.key}`,
                    label: (
                        <>
                            {group.label}
                            {': '}
                            {entries.length}
                        </>
                    ),
                },
            ];
        }

        return entries.map((entry) => buildChip(entry.chipKey, 'REVIEWS__FILTER__LABEL__INCLUDES_TAGS', entry.title));
    });
}

// Resolves the applied review filters into human-readable chips for the PDF report (BST-10539).
// Tag-label lookups only fire when tag filters are actually applied.
export function useReviewFilterPdfChips(
    reviewFilters: ReviewsAndAnswersReviewFiltersType
): Array<ReviewFilterPdfChipType> {
    const catalogIds = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.CatalogIds];
    const rating = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Rating];
    const sentiments = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Sentiments];
    const tagIds = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.TagIds];
    const withoutText = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.WithoutText];
    const isReplied = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.IsReplied];
    const actionReply = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.ActionReply];
    const target = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Target];
    const includeDeleted = reviewFilters[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted];

    const hasTagIds = tagIds.length > 0;

    const {data: catalogList} = useReviewsCatalogList();
    const {data: manualTags} = useAllManualTags(hasTagIds);
    const {data: aspects} = useReviewsAspects(hasTagIds);
    const {data: qrCodeTags} = useQRCodesTags(hasTagIds);
    const {data: mailingsTags} = useMailingsTags(hasTagIds);

    return useMemo(
        () => [
            ...buildCatalogChips(catalogIds, catalogList),
            ...buildRatingChips(rating),
            ...buildSentimentChips(sentiments),
            ...buildTagChips(tagIds, {aspects, manualTags, qrCodeTags, mailingsTags}),
            ...buildOptionChip(
                'content-of-reviews',
                'REVIEWS__FILTER__LABEL__CONTENT_OF_REVIEWS',
                CONTENT_OF_REVIEWS_OPTIONS,
                withoutText
            ),
            ...buildOptionChip(
                'replies-to-reviews',
                'REVIEWS__FILTER__LABEL__REPLIES_TO_REVIEWS',
                REPLIES_TO_REVIEWS_OPTIONS,
                isReplied
            ),
            ...buildOptionChip(
                'possibility-of-answer',
                'REVIEWS__FILTER__LABEL__POSSIBILITY_OF_AN_ANSWER',
                POSSIBILITY_OF_AN_ANSWER_OPTIONS,
                actionReply
            ),
            ...buildOptionChip('review-type', 'REVIEWS__FILTER__LABEL__REVIEW_TYPE', REVIEW_TYPE_OPTIONS, target),
            // Unlike the toggles above, this filter has no "unset" state — it always scopes the
            // report to include/exclude deleted reviews, so surface it regardless of the value.
            buildChip(
                'include-deleted',
                'REVIEW_AND_ANSWER_STATISTICS__FILTER__INCLUDE_DELETED',
                getOptionLabel(INCLUDE_DELETED_OPTIONS, includeDeleted)
            ),
        ],
        [
            catalogList,
            catalogIds,
            rating,
            sentiments,
            tagIds,
            aspects,
            manualTags,
            qrCodeTags,
            mailingsTags,
            withoutText,
            isReplied,
            actionReply,
            target,
            includeDeleted,
        ]
    );
}
